<template>
  <header>
    <HeaderSection
      :info-link="{
        text: 'Help Center',
        url: 'https://hc.erank.com/en/articles/6963909-monthly-trends'
      }"
      info-body="Discover what keywords are trending now on Etsy! Updated daily, we flag yesterday’s most popular search terms and show you last month’s Top 100 most-searched keywords for each platform, along with 15-month trend graphs of historical data."
      info-title="Monthly Trends"
      title="Monthly Trends"
    >
      <template #sub-title>
        The most popular search terms on Etsy for
        <template v-if="date">
          {{ format(date, 'MMMM yyyy') }}
        </template>
        <Skeleton
          v-else
          class="ms-2 inline-block"
          height="0.75rem"
          width="4rem"
        />. Keywords marked with
        <Icon
          class="!inline-block size-3.5 text-red-500"
          name="trending-fire"
        />
        were very popular yesterday.
      </template>
    </HeaderSection>
    <div class="mt-8 md:mt-14">
      <div class="flex items-center justify-between">
        <Button
          :class="{
            'border-[--v3-button-infobuttonbg] bg-[--v3-button-infobuttonbg]': isFiltersOpen
          }"
          :outlined="!isFiltersOpen"
          :severity="isFiltersOpen ? 'info': 'secondary'"
          class="justify-self-end [grid-area:filter-button] md:hidden"
          label="Filters"
          @click="isFiltersOpen = !isFiltersOpen"
        />
      </div>
      <div
        v-if="isMd || isFiltersOpen"
        class="mt-4 grid gap-8 rounded-md border border-[#dfe7ef] bg-[#f6f9fc] p-4 [grid-area:filters] md:grid-cols-3"
      >
        <div class="flex flex-col gap-1.5">
          <label class="v3-small-semibold-600-line-height-auto uppercase">Filter</label>
          <InputGroup class="flex items-center rounded-md border border-[--v3-input-field-inputborder] bg-white" unstyled>
            <InputGroupAddon class="flex items-center justify-center px-3" unstyled>
              <Icon
                class="size-3.5 text-[--v3-input-field-inputiconcolor]"
                name="trending-search"
              />
            </InputGroupAddon>
            <InputText
              :model-value="searchParams.query"
              class="v3-body-regular-400-line-height-auto none-focus w-full rounded-md py-[calc(10.5px-theme(spacing.px))] hover:!border-none focus:outline-none"
              placeholder="Filter keywords..."
              unstyled
              @update:model-value="handleSearchChange"
            />
          </InputGroup>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="v3-small-semibold-600-line-height-auto uppercase">Categories</label>
          <Dropdown
            :loading="isPendingAvailability"
            :model-value="params.category"
            :options="categoryGroups"
            filter-placeholder="Find categories"
            option-group-label="label"
            option-group-children="items"
            option-label="name"
            option-value="value"
            @update:model-value="handleRouteParamChange('category', $event as string)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="v3-small-semibold-600-line-height-auto uppercase">Date</label>
          <Dropdown
            :loading="isPendingAvailability"
            :model-value="date ? format(date, 'yyyyMM') : undefined"
            :options="dates"
            filter-placeholder="Find year"
            option-label="name"
            option-value="value"
            @update:model-value="handleRouteParamChange('date', $event as string)"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { useMediaQuery } from '@vueuse/core';
import { format, isValid, parse } from 'date-fns';
import { debounce, keyBy } from 'lodash';
import Button from 'primevue/button';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';
import Skeleton from 'primevue/skeleton';
import { computed, ref } from 'vue';

import Icon from '@/common/components/atoms/Icon';
import Dropdown from '@/common/components/molecules/Dropdown';
import HeaderSection from '@/common/components/molecules/HeaderSection';
import {
  trendingReportAvailabilityQueryOptions,
  useParams,
  useSearchParams,
} from '@/modules/trending/composables';
import { categories, pod } from '@/modules/trending/constants';

const isMd = useMediaQuery('((min-width: 768px))');
const { params, updateParams } = useParams();
const { searchParams, updateSearchParams } = useSearchParams();
const {
  data: availability,
  isPending: isPendingAvailability,
} = useQuery(trendingReportAvailabilityQueryOptions());

const isFiltersOpen = ref(false);

const availabilityByDate = computed(() => keyBy(availability.value, 'date'));
const date = computed(() => {
  if (params.value.date) {
    const date = parse(params.value.date, 'yyyyMM', new Date());

    if (isValid(date)) {
      return date;
    }
  }

  if ((availability.value?.length ?? 0) > 0) {
    return parse(availability.value[0].date, 'yyyyMM', new Date());
  }

  return null;
});
const dates = computed(() =>
  availability.value
    ?.filter((item) =>
      availabilityByDate.value[item.date]
        ?.categories
        .includes(params.value.category),
    )
    .map((item) => {
      const date = parse(item.date, 'yyyyMM', new Date());

      return {
        name: format(date, 'MMM yyyy'),
        value: format(date, 'yyyyMM'),
      };
    }),
);

const categoryGroups = computed(() => {
  const item = availabilityByDate.value[params.value.date];
  const _categories = categories.filter(
    (category) => item?.categories.includes(category.value),
  );
  const _pod = pod.filter(
    (category) => item?.categories.includes(category.value),
  );

  const groups = [];

  if (_categories.length > 0) {
    groups.push({
      code: 'categories',
      items: _categories,
      label: 'Categories',
    });
  }

  if (_pod.length > 0) {
    groups.push({
      code: 'pod',
      items: _pod,
      label: 'POD',
    });
  }

  return groups;
});

function handleRouteParamChange(param: 'category' | 'date', value: string) {
  updateParams({
    ...params.value,
    [param]: value,
  });
}

const handleSearchChange = debounce((search: string) => {
  updateSearchParams({
    query: search || undefined,
  });
}, 300);
</script>
